/**
 * ONE ladder from a body query's state to an `InlineMessageBody` state — the message page, the
 * modal and the timeline's expanded row all read it here
 * (`docs/specs/strategic-architecture/timeline-inline-message-spec.md` §6: page, modal and row
 * cannot drift into three stories about one message).
 *
 * ⛔ `isError` IS A BRANCH OF ITS OWN, and it is the one a copied ladder loses (review F3). Reading
 * the status off `error instanceof ApiError` answers "which refusal was it", never "did this fail":
 * a plain `Error` (the 401 re-auth path throws one) or a `TypeError` (offline, DNS, a cancelled
 * fetch) carries no status, so a ladder that falls through to `data != null ? 'ready' : 'loading'`
 * leaves the reader on "Checking entitlement…" forever, with no error and no retry — the worst of
 * the possible wrong answers on a governed surface, because it reads as "still deciding whether you
 * may see this".
 *
 * Kept in its own module rather than inside `InlineMessageBody.tsx` so that component stays
 * presentational: this one knows about `ApiError`, i.e. about the door.
 */
import { ApiError } from '../../api/client';
import type { InlineMessageState } from './InlineMessageBody';

/** The part of a React Query result this decision actually needs. */
export interface InlineMessageQueryLike {
  isLoading: boolean;
  /** ⛔ A REFETCH IS LOADING. See the note on the ladder below. Optional so the many existing
   *  callers that hand-build a query-like object are unaffected; React Query always supplies it. */
  isFetching?: boolean;
  isError?: boolean;
  error?: unknown;
  data?: string | null;
}

/**
 * EC-5 — the body ladder, WIDENED by what the content envelope says about acquisition.
 *
 * The body query alone cannot tell "nothing is stored" from "nothing is stored and we are fetching
 * it right now": both are a 404 on the byte door. The status envelope is the only thing that knows,
 * so the two are combined HERE rather than in each surface, and `missing` is overridden only in the
 * directions the envelope can prove (spec §3.2).
 */
export function inlineMessageStateWithContent(
  q: InlineMessageQueryLike,
  body: { state?: string; can_fetch?: boolean } | undefined,
  opts: { pending?: boolean } = {},
): InlineMessageState {
  const base = inlineMessageStateFromQuery(q);
  if (base !== 'missing') return base;   // never override a refusal, an error or a real body
  if (opts.pending || body?.state === 'fetching') return 'retrieving';
  if (body?.state === 'unavailable') return 'source_gone';
  return 'missing';
}


export function inlineMessageStateFromQuery(q: InlineMessageQueryLike): InlineMessageState {
  // ⛔ **A REFETCH IN FLIGHT IS `loading`, NOT "still ready"** (governed-email-content spec §3.3,
  // acceptance G8/G9; review MAJOR 2). React Query keeps the last successful `data` on the query
  // object while it re-runs, and it keeps it even when the re-run FAILS — so a reader whose
  // entitlement was revoked went on reading the previous body, from cache, while the 403 was in
  // flight and after it landed. `staleTime: 0` does not help: it controls WHEN a refetch starts,
  // never what renders while one is running. Suppressing the previous content until this open's
  // authorization succeeds is the actual requirement, and this is the single line that does it for
  // every body surface at once.
  if (q.isLoading || q.isFetching === true) return 'loading';
  const status = q.error instanceof ApiError ? q.error.status : null;
  if (status === 403) return 'forbidden';
  if (status === 404) return 'missing';
  if (status != null) return 'error';
  if (q.isError === true || q.error != null) return 'error';   // see the ⛔ above
  if (q.data != null) return 'ready';
  return 'loading';
}
